import TTLCache from '@isaacs/ttlcache';
import { isNil } from 'lodash-es';
import { err, ok } from '@whr/core_utils/result.js';
import { ConfigurationNotFoundError } from './error/configuration_not_found_in_storage.js';
import { SpecificationNotFoundError } from './error/specification_not_found_error.js';
import { StorageProviderNotFoundError } from './error/storage_provider_not_found_error.js';
import { TenantUndefinedError } from './error/tenant_undefined_error.js';
import { ConfigurationUnknownError } from './error/unknown_error.js';
export class ConfigurationService {
    constructor({ storageProviders, specifications, getTenantId }) {
        this.cache = new TTLCache({ noUpdateTTL: true, updateAgeOnGet: false });
        this.storageProviders = storageProviders;
        this.getTenantId = getTenantId;
        this.specifications = Object.fromEntries(Object.entries(specifications).map(([key, specification]) => [key, {
                ...specification,
                isSensitive: specification.isSensitive ?? true,
                isOptional: specification.isOptional ?? false
            }]));
    }
    async get(key) {
        const cachedValue = this.cache.get(key);
        if (!isNil(cachedValue))
            return ok(cachedValue);
        const specification = this.specifications[key];
        if (isNil(specification)) {
            return err(new SpecificationNotFoundError(`Specification for key ${key} not found`, { key, specifications: this.specifications }));
        }
        const storageProvider = this.storageProviders[specification.storageProvider];
        if (isNil(storageProvider)) {
            return err(new StorageProviderNotFoundError(`Storage provider ${specification.storageProvider} not found`, { key, storageProviders: this.storageProviders }));
        }
        const tenantId = await this.getTenantId();
        if (tenantId.isErr()) {
            return err(new ConfigurationUnknownError('An unknown error occurred when trying to get tenant id', tenantId.error));
        }
        const getResult = await storageProvider.get(key, specification.scope, tenantId.value);
        if (getResult.isErr()) {
            if (getResult.error instanceof ConfigurationNotFoundError && specification.isOptional)
                return ok(undefined);
            return err(getResult.error);
        }
        if (!isNil(getResult.value)) {
            this.cache.set(key, getResult.value, { ttl: specification.cacheTTLMs });
        }
        return ok(getResult);
    }
    async set(key, value) {
        const specification = this.specifications[key];
        if (isNil(specification)) {
            return err(new SpecificationNotFoundError(`Specification for key ${key} not found`, { key, specifications: this.specifications }));
        }
        const storageProvider = this.storageProviders[specification.storageProvider];
        if (isNil(storageProvider)) {
            return err(new StorageProviderNotFoundError(`Storage provider ${specification.storageProvider} not found`, { key, storageProviders: this.storageProviders }));
        }
        const tenantId = await this.getTenantId();
        if (tenantId.isErr()) {
            return err(new ConfigurationUnknownError('An unknown error occurred when trying to get tenant id', tenantId.error));
        }
        if (isNil(tenantId.value)) {
            return err(new TenantUndefinedError('The tenant is undefined'));
        }
        const setResult = await storageProvider.set(key, specification.scope, value, tenantId.value);
        if (setResult.isErr())
            return err(setResult.error);
        this.cache.set(key, value, { ttl: specification.cacheTTLMs });
        return ok();
    }
    async isSensitive(key) {
        const specification = this.specifications[key];
        if (isNil(specification)) {
            return err(new SpecificationNotFoundError(`Specification for key ${key} not found`, { key, specifications: this.specifications }));
        }
        return ok(specification.isSensitive);
    }
    async isOptional(key) {
        const specification = this.specifications[key];
        if (isNil(specification)) {
            return err(new SpecificationNotFoundError(`Specification for key ${key} not found`, { key, specifications: this.specifications }));
        }
        return ok(specification.isOptional);
    }
}
//# sourceMappingURL=index.js.map